// src/components/PlacedTooltipsLayer.tsx
import React, { useEffect, useState } from 'react';
import type { PlacedTooltipRecord } from './TooltipManager';

const API_BASE = '/api/placed-tooltips';
const GAP = 8;

function positionNextToElement(
  rect: DOMRect,
  placement: string,
  tooltipWidth: number,
  tooltipHeight: number
): { left: number; top: number; transform: string } {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  switch (placement) {
    case 'top': {
      let left = centerX - tooltipWidth / 2;
      left = Math.max(GAP, Math.min(w - tooltipWidth - GAP, left));
      return {
        left,
        top: rect.top - tooltipHeight - GAP,
        transform: 'translate(0, 0)',
      };
    }
    case 'bottom': {
      let left = centerX - tooltipWidth / 2;
      left = Math.max(GAP, Math.min(w - tooltipWidth - GAP, left));
      return {
        left,
        top: rect.bottom + GAP,
        transform: 'translate(0, 0)',
      };
    }
    case 'left': {
      let top = centerY - tooltipHeight / 2;
      top = Math.max(GAP, Math.min(h - tooltipHeight - GAP, top));
      return {
        left: rect.left - tooltipWidth - GAP,
        top,
        transform: 'translate(0, 0)',
      };
    }
    case 'right': {
      let top = centerY - tooltipHeight / 2;
      top = Math.max(GAP, Math.min(h - tooltipHeight - GAP, top));
      return {
        left: rect.right + GAP,
        top,
        transform: 'translate(0, 0)',
      };
    }
    default: {
      let left = centerX - tooltipWidth / 2;
      left = Math.max(GAP, Math.min(w - tooltipWidth - GAP, left));
      return {
        left,
        top: rect.bottom + GAP,
        transform: 'translate(0, 0)',
      };
    }
  }
}

const TRIGGER_SIZE = 20;

function TooltipMarker({
  style,
  content,
}: {
  style: React.CSSProperties;
  content: string;
}) {
  const [hover, setHover] = useState(false);

  return (
    <div
      className="relative inline-flex pointer-events-auto"
      style={style}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span
        className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-400/50 text-gray-600 text-xs font-medium cursor-help hover:bg-gray-400/70"
        aria-label="Help"
      >
        ?
      </span>
      {hover && (
        <div className="absolute left-1/2 bottom-full mb-1 -translate-x-1/2 z-10 px-4 py-2 text-sm text-white bg-gray-700 rounded-lg shadow-lg whitespace-normal max-w-md min-w-[12rem]">
          {content}
        </div>
      )}
    </div>
  );
}

function TooltipWithAnchors({ t }: { t: PlacedTooltipRecord }) {
  // For a selector we may match multiple elements. Previously we used querySelector (first match only),
  // which caused tooltips to appear on only one instance in repeated lists.
  const [styles, setStyles] = useState<React.CSSProperties[]>([]);

  useEffect(() => {
    const selector = t.anchor_selector?.trim();
    const placement = t.anchor_placement || 'bottom';
    if (!selector) return;

    let rafId: number | null = null;
    const update = () => {
      const elements = Array.from(document.querySelectorAll(selector));
      if (elements.length === 0) {
        setStyles([]);
        return;
      }

      const nextStyles = elements
        .map((el) => {
          const rect = el.getBoundingClientRect();
          // If the anchor is off-screen, hide the tooltip marker instead of
          // clamping it into the viewport (which looks like a sticky floating "?").
          if (
            rect.bottom <= 0 ||
            rect.top >= window.innerHeight ||
            rect.right <= 0 ||
            rect.left >= window.innerWidth
          ) {
            return null;
          }
          const pos = positionNextToElement(rect, placement, TRIGGER_SIZE, TRIGGER_SIZE);
          return {
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: pos.transform,
          } as React.CSSProperties;
        })
        // Filter out elements with no size to avoid weird "0,0" placements.
        .filter((s): s is React.CSSProperties => !!s && Number.isFinite(s.left) && Number.isFinite(s.top));

      setStyles(nextStyles);
    };

    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    update();
    const onUpdate = scheduleUpdate;
    window.addEventListener('resize', onUpdate);
    window.addEventListener('scroll', onUpdate, true);
    const mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });
    const interval = setInterval(scheduleUpdate, 250);

    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('resize', onUpdate);
      window.removeEventListener('scroll', onUpdate, true);
      mutationObserver.disconnect();
      clearInterval(interval);
    };
  }, [t.anchor_selector, t.anchor_placement]);

  if (styles.length === 0) return null;

  return (
    <>
      {styles.map((style, idx) => (
        <TooltipMarker
          key={`${t.id}-${idx}`}
          style={style}
          content={t.content}
        />
      ))}
    </>
  );
}

function TooltipPercent({ t }: { t: PlacedTooltipRecord }) {
  const [hover, setHover] = useState(false);

  return (
    <div
      className="absolute pointer-events-auto"
      style={{
        left: `${t.x_percent}%`,
        top: `${t.y_percent}%`,
        transform: 'translate(-50%, -50%)',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span
        className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-400/50 text-gray-600 text-xs font-medium cursor-help hover:bg-gray-400/70"
        aria-label="Help"
      >
        ?
      </span>
      {hover && (
        <div className="absolute left-1/2 bottom-full mb-1 -translate-x-1/2 z-10 px-4 py-2 text-sm text-white bg-gray-700 rounded-lg shadow-lg whitespace-normal max-w-md min-w-[12rem]">
          {t.content}
        </div>
      )}
    </div>
  );
}

interface PlacedTooltipsLayerProps {
  pageKey: string | null;
}

const PlacedTooltipsLayer: React.FC<PlacedTooltipsLayerProps> = ({ pageKey }) => {
  const [tooltips, setTooltips] = useState<PlacedTooltipRecord[]>([]);

  useEffect(() => {
    // Clear immediately so old page markers disappear while new page tooltips load.
    if (!pageKey) {
      setTooltips([]);
      return;
    }

    setTooltips([]);
    let cancelled = false;
    fetch(`${API_BASE}/?page_key=${encodeURIComponent(pageKey)}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setTooltips(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setTooltips([]);
      });
    return () => { cancelled = true; };
  }, [pageKey]);

  if (!pageKey || tooltips.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[9000] pointer-events-none"
      aria-hidden
    >
      {tooltips.map((t) =>
        t.anchor_selector ? (
          <TooltipWithAnchors key={t.id} t={t} />
        ) : (
          <TooltipPercent key={t.id} t={t} />
        )
      )}
    </div>
  );
};

export default PlacedTooltipsLayer;
